// Экран входа: сегмент источника (Lichess/Chess.com/Авто) + поле ника с
// валидацией через isValidNick из @ext, префилл из localStorage по tg user id.
import { isValidNick, normalizeNick } from '@ext/core/game-sources';
import { t } from '../i18n';
import { setQuery, setPgnGame } from '../state';
import { coachGameFromPgn, diagnosePgn } from '../core/pgn-import';
import { trackNickEntered } from '../services/track';
import {showMainButton, hideMainButton, tgUserKey, inTelegram, canAddToHomeScreen, addToHomeScreen} from '../tg';
import type { GameSource } from '../services/games';

function nickKey(): string {
  return `cc_nick_${tgUserKey()}`;
}

function loadNick(): string {
  try {
    return localStorage.getItem(nickKey()) || '';
  } catch {
    return '';
  }
}

function saveNick(nick: string): void {
  try {
    localStorage.setItem(nickKey(), nick);
  } catch {
    /* storage may be blocked */
  }
}

export function renderLaunch(root: HTMLElement): void {
  root.className = 'app v2 with-main launch-flex';
  root.innerHTML = `
    <header class="topbar">
      <span class="back" data-close>${t('top.close')}</span>
      <span class="top-title">${t('launch.brand')}</span>
      <span class="top-action"></span>
    </header>

    <section class="hero hero-compact">
      <div class="hero-row">
        <div class="logo logo-sm">${t('launch.brand')}</div>
        <div>
          <div class="eyebrow">${t('launch.eyebrow')}</div>
          <h1 class="h1-compact">${t('launch.title')}</h1>
        </div>
      </div>
      <p class="muted hero-sub">${t('launch.subtitle')}</p>
    </section>

    <section class="card form-card">
      <div class="eyebrow">${t('launch.source_label')}</div>
      <h2 style="margin:5px 0 14px">${t('launch.source_q')}</h2>
      <div class="segmented" role="group" aria-label="${t('launch.source_label')}">
        <button type="button" data-src="lichess">${t('launch.src_lichess')}</button>
        <button type="button" data-src="chesscom">${t('launch.src_chesscom')}</button>
        <button type="button" data-src="auto">${t('launch.src_auto')}</button>
      </div>
      <label style="display:block;margin:16px 0 7px;font-size:12px;font-weight:700" for="nickname">${t('launch.nick_label')}</label>
      <input id="nickname" class="field" autocapitalize="none" autocomplete="off" spellcheck="false"
        placeholder="${t('launch.nick_placeholder')}" aria-label="${t('launch.nick_label')}">
      <p class="tiny source-note" data-error-slot>${t('launch.source_note')}</p>
      <div class="pgn-import">
        <button type="button" class="pgn-toggle" data-pgn-toggle aria-expanded="false">${t('launch.pgn_toggle')}</button>
        <div class="pgn-panel hidden" data-pgn-panel>
          <textarea class="field pgn-area" data-pgn-input rows="5" autocapitalize="none" autocomplete="off"
            spellcheck="false" placeholder="${t('launch.pgn_placeholder')}" aria-label="${t('launch.pgn_toggle')}"></textarea>
          <p class="tiny field-error hidden" data-pgn-error></p>
          <div class="row gap12 pgn-actions">
            <button type="button" class="ghost-btn grow" data-pgn-paste>${t('launch.pgn_paste')}</button>
            <button type="button" class="inline-cta grow" data-pgn-analyze>${t('launch.pgn_analyze')}</button>
          </div>
        </div>
      </div>
    </section>

    <p class="tiny bottom-note">${t('launch.bottom_note')}</p>
    <div style="text-align:center"><button type="button" class="home-screen-btn hidden" data-home-btn>📱 ${t('launch.add_home')}</button></div>
    <button type="button" class="main-button browser-main-button" data-cta>${t('launch.cta')}</button>
  `;

  const homeBtn = root.querySelector<HTMLButtonElement>('[data-home-btn]');
  if (homeBtn) {
    canAddToHomeScreen((can) => homeBtn.classList.toggle('hidden', !can));
    homeBtn.addEventListener('click', () => addToHomeScreen());
  }

  let source: GameSource = 'auto';
  const seg = root.querySelector<HTMLElement>('.segmented')!;
  const input = root.querySelector<HTMLInputElement>('#nickname')!;
  const errSlot = root.querySelector<HTMLElement>('[data-error-slot]')!;

  function selectSource(next: GameSource): void {
    source = next;
    seg.querySelectorAll<HTMLButtonElement>('[data-src]').forEach((b) => {
      const on = b.dataset.src === next;
      b.classList.toggle('active', on);
      b.setAttribute('aria-pressed', String(on));
    });
  }
  selectSource(source);
  seg.querySelectorAll<HTMLButtonElement>('[data-src]').forEach((b) => {
    b.addEventListener('click', () => selectSource(b.dataset.src as GameSource));
  });

  input.value = loadNick();

  function submit(): void {
    const nick = normalizeNick(input.value);
    if (!isValidNick(nick)) {
      input.classList.add('invalid');
      errSlot.textContent = t('launch.err_nick');
      errSlot.classList.add('field-error');
      return;
    }
    saveNick(nick);
    trackNickEntered(source);
    setQuery(source, nick);
    location.hash = '#/games';
  }

  input.addEventListener('input', () => {
    input.classList.remove('invalid');
    errSlot.classList.remove('field-error');
    errSlot.textContent = t('launch.source_note');
  });
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') submit();
  });
  // Клавиатура (interactive-widget=resizes-content) ужимает вьюпорт — мягко доводим
  // поле в зону видимости, чтобы экран не «прыгал» и ввод не уезжал под клавиатуру.
  input.addEventListener('focus', () => {
    setTimeout(() => input.scrollIntoView({ block: 'center', behavior: 'smooth' }), 250);
  });

  root.querySelector<HTMLButtonElement>('[data-cta]')!.addEventListener('click', submit);

  wirePgnImport(root);

  if (inTelegram) showMainButton(t('launch.cta'), submit);
  else hideMainButton();
}

/** Свернутый блок «или вставь PGN»: раскрытие, вставка из буфера, валидация → разбор. */
function wirePgnImport(root: HTMLElement): void {
  const toggle = root.querySelector<HTMLButtonElement>('[data-pgn-toggle]')!;
  const panel = root.querySelector<HTMLElement>('[data-pgn-panel]')!;
  const input = root.querySelector<HTMLTextAreaElement>('[data-pgn-input]')!;
  const errEl = root.querySelector<HTMLElement>('[data-pgn-error]')!;

  const clearErr = (): void => {
    errEl.classList.add('hidden');
    input.classList.remove('invalid');
  };

  toggle.addEventListener('click', () => {
    const collapsed = panel.classList.toggle('hidden');
    toggle.setAttribute('aria-expanded', String(!collapsed));
    if (!collapsed) setTimeout(() => input.focus(), 50);
  });
  input.addEventListener('input', clearErr);

  root.querySelector<HTMLButtonElement>('[data-pgn-paste]')!.addEventListener('click', () => {
    // navigator.clipboard.readText может отсутствовать/бросать в TG-webview — молча глотаем.
    void (async () => {
      try {
        const text = await navigator.clipboard?.readText();
        if (text) {
          input.value = text;
          clearErr();
        }
      } catch {
        /* буфер недоступен — пользователь вставит вручную */
      }
    })();
  });

  const openGame = (pgn: string): boolean => {
    try {
      const game = coachGameFromPgn(pgn);
      setPgnGame(game);
      location.hash = `#/game/${encodeURIComponent(game.id)}`;
      return true;
    } catch {
      return false;
    }
  };

  root.querySelector<HTMLButtonElement>('[data-pgn-analyze]')!.addEventListener('click', () => {
    if (openGame(input.value)) return;
    // битая запись: показать, какой именно ход не читается, и предложить разбор до него
    const diag = diagnosePgn(input.value);
    input.classList.add('invalid');
    errEl.classList.remove('hidden');
    if (!diag) {
      errEl.textContent = t('launch.pgn_err');
      return;
    }
    const moveLabel = `${diag.moveNo}${diag.white ? '.' : '...'} ${diag.badSan}`;
    errEl.textContent = t('launch.pgn_err_move', { move: moveLabel });
    if (diag.prefixPgn) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ghost-btn';
      btn.style.marginTop = '8px';
      btn.textContent = t('launch.pgn_prefix');
      const prefix = diag.prefixPgn;
      btn.addEventListener('click', () => void openGame(prefix));
      errEl.appendChild(document.createElement('br'));
      errEl.appendChild(btn);
    }
  });
}
